import type { EditorState } from '@milkdown/kit/prose/state';
import { Plugin, PluginKey } from '@milkdown/kit/prose/state';
import type { EditorView } from '@milkdown/kit/prose/view';
import { $prose } from '@milkdown/kit/utils';
import { Fragment } from '@milkdown/kit/prose/model';

import { cellTextToBreakFragment, hardBreakType, isInTableCellPos } from './tableCellHardBreak';

export function isInTableCell(state: EditorState): boolean {
  const { $from } = state.selection;

  return isInTableCellPos($from);
}

/**
 * ProseMirror plugin: Shift+Enter inserts a hard break when the selection is inside a table cell or header.
 * Exported for unit tests; use {@link tableHardBreakPlugin} in Milkdown editors.
 */
export function createTableHardBreakProsePlugin() {
  return new Plugin({
    key: new PluginKey('table-hard-break'),
    appendTransaction(_trs, _old, newState) {
      if (!hardBreakType(newState.schema)) return null;

      const cuts: { from: number; to: number; frag: Fragment }[] = [];

      newState.doc.descendants((node, pos) => {
        if (!node.isText) return;
        if (!isInTableCellPos(newState.doc.resolve(pos))) return;

        const raw = node.text ?? '';
        const frag = cellTextToBreakFragment(newState.schema, raw, node.marks);
        if (!frag?.childCount) return;

        cuts.push({ from: pos, to: pos + raw.length, frag });
      });

      if (!cuts.length) return null;
      cuts.sort((a, b) => b.from - a.from);
      const tr = newState.tr;
      for (const c of cuts) tr.replaceWith(c.from, c.to, c.frag);

      return tr;
    },
    props: {
      handleKeyDown(view: EditorView, event: KeyboardEvent) {
        if (event.key === 'Enter' && event.shiftKey) {
          const { state, dispatch } = view;
          const hardBreak = hardBreakType(state.schema);
          if (!hardBreak || !isInTableCellPos(state.selection.$from)) {
            return false;
          }

          dispatch(state.tr.replaceSelectionWith(hardBreak.create()).scrollIntoView());

          return true;
        }
        
return false;
      },
    },
  });
}

/**
 * Milkdown plugin that enables Shift+Enter to insert a hard break inside table cells.
 * Plain Enter still follows preset-gfm (e.g. exit table); this only adds line breaks within a cell.
 */
export const tableHardBreakPlugin = $prose(() => createTableHardBreakProsePlugin());
